// src/pages/Login.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../services/supabase';
import { validateEmail, normalizeEmail, normalizePhone } from '../utils/validation';
import {
    PHONE_CODES,
    validatePhoneNumber,
    formatPhoneNumber,
    getPhoneExample
} from '../utils/phoneUtils';

// Компонент модального окна для восстановления пароля
const ResetPasswordModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    onSend: (email: string) => Promise<void>;
}> = ({ isOpen, onClose, onSend }) => {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!validateEmail(email)) {
            setError('Введите корректный email');
            return;
        }

        setLoading(true);
        try {
            await onSend(normalizeEmail(email));
            setEmail('');
            onClose();
        } catch (err) {
            setError('Ошибка отправки. Попробуйте позже.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                    <h3>Восстановление пароля</h3>
                    <button className="modal-close" onClick={onClose}>
                        <XMarkIcon style={{ width: 20, height: 20 }} />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <p className="modal-description">
                        Введите ваш email, и мы отправим ссылку для сброса пароля
                    </p>
                    <input
                        type="email"
                        placeholder="Email *"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className={`auth-input ${error ? 'error' : ''}`}
                        autoComplete="email"
                        required
                    />
                    {error && <div className="error-message">{error}</div>}
                    <div className="modal-buttons">
                        <button type="submit" disabled={loading} className="auth-button">
                            {loading ? 'Отправка...' : 'Отправить ссылку'}
                        </button>
                        <button type="button" onClick={onClose} className="auth-button secondary">
                            Отмена
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Компонент вкладок
const TabButton: React.FC<{
    active: boolean;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ active, onClick, children }) => (
    <button
        type="button"
        onClick={onClick}
        className={`auth-tab ${active ? 'active' : ''}`}
    >
        {children}
    </button>
);

export const Login: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, signIn, sendPasswordReset } = useAuth();
    const [showPassword, setShowPassword] = useState(false);
    const [selectedPhoneCode, setSelectedPhoneCode] = useState('+992');
    const [showResetModal, setShowResetModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const isMountedRef = useRef(true);

    // Вкладка входа
    const [loginTab, setLoginTab] = useState<'phone' | 'email'>('phone');

    // Поля входа
    const [loginEmail, setLoginEmail] = useState('');
    const [loginPhone, setLoginPhone] = useState('');
    const [loginPassword, setLoginPassword] = useState('');

    const from = (location.state as any)?.from?.pathname || '/';

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Проверка успешного сброса пароля
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('reset') === 'success') {
            toast.success('Пароль успешно изменен! Войдите с новым паролем', {
                duration: 5000,
            });
            // Очищаем параметр из URL без перезагрузки
            const newUrl = window.location.pathname;
            window.history.replaceState({}, '', newUrl);
        }
    }, [location]);

    useEffect(() => {
        if (user && isMountedRef.current) {
            navigate(from, { replace: true });
        }
    }, [user, navigate, from]);

    const handlePhoneCodeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedPhoneCode(e.target.value);
        setLoginPhone('');
    };

    const handlePhoneChange = (value: string) => {
        const formatted = formatPhoneNumber(value, selectedPhoneCode);
        setLoginPhone(formatted);
    };

    // src/pages/Login.tsx - исправленный handleLogin

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();

        let identifier = '';

        if (loginTab === 'email') {
            if (!loginEmail) {
                toast.error('Введите email');
                return;
            }
            if (!validateEmail(loginEmail)) {
                toast.error('Введите корректный email');
                return;
            }
            identifier = loginEmail;
        } else {
            if (!loginPhone) {
                toast.error('Введите номер телефона');
                return;
            }
            const cleanPhone = normalizePhone(loginPhone);
            if (!validatePhoneNumber(cleanPhone, selectedPhoneCode)) {
                toast.error(`Неверный формат номера. Пример: ${getPhoneExample(selectedPhoneCode)}`);
                return;
            }
            identifier = cleanPhone;
        }

        if (!loginPassword) {
            toast.error('Введите пароль');
            return;
        }

        setLoading(true);
        const result = await signIn(identifier, loginPassword);

        if (isMountedRef.current) {
            if (result.success) {
                // ✅ ПОЛУЧАЕМ СОХРАНЕННЫЙ ПУТЬ ДЛЯ РЕДИРЕКТА
                const redirectPath = sessionStorage.getItem('redirectAfterLogin');

                if (redirectPath && redirectPath !== '/login' && redirectPath !== '/register') {
                    // Очищаем сохраненный путь
                    sessionStorage.removeItem('redirectAfterLogin');
                    // Редиректим на сохраненный путь
                    navigate(redirectPath, { replace: true });
                } else {
                    // Редиректим на главную
                    navigate('/', { replace: true });
                }
            } else if (result.error) {
                toast.error(result.error);
            }
            setLoading(false);
        }
    };

    const handleForgotPassword = () => {
        setShowResetModal(true);
    };

    const handleSendPasswordReset = async (email: string) => {
        console.log('📧 Sending password reset to:', email);
        const result = await sendPasswordReset(email);
        console.log('📧 Reset result:', result);
        if (result.success) {
            setShowResetModal(false);
        } else {
            toast.error(result.error || 'Ошибка отправки');
            throw new Error(result.error);
        }
    };

    return (
        <div className="auth-container">
            <div className="auth-card">
                <h1 className="auth-title">Ruyo Market</h1>
                <p className="auth-subtitle">Войдите в свой аккаунт</p>

                <form onSubmit={handleLogin} className="auth-form">
                    {/* Вкладки входа */}
                    <div className="auth-tabs">
                        <TabButton
                            active={loginTab === 'phone'}
                            onClick={() => setLoginTab('phone')}
                        >
                            По телефону
                        </TabButton>
                        <TabButton
                            active={loginTab === 'email'}
                            onClick={() => setLoginTab('email')}
                        >
                            По email
                        </TabButton>
                    </div>

                    {/* Поле ввода */}
                    {loginTab === 'phone' ? (
                        <div className="phone-input-group">
                            <select
                                value={selectedPhoneCode}
                                onChange={handlePhoneCodeChange}
                                className="country-code-select"
                                style={{ fontFamily: 'monospace', fontSize: '16px' }}
                            >
                                {PHONE_CODES.map((item) => (
                                    <option key={item.code} value={item.code}>
                                        {item.flag} {item.code}
                                    </option>
                                ))}
                            </select>
                            <input
                                type="tel"
                                placeholder={getPhoneExample(selectedPhoneCode)}
                                value={loginPhone}
                                onChange={(e) => handlePhoneChange(e.target.value)}
                                className="auth-input phone-input"
                                autoComplete="tel"
                            />
                        </div>
                    ) : (
                        <input
                            type="email"
                            placeholder="Email"
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            className="auth-input"
                            autoComplete="email"
                        />
                    )}

                    {/* Поле пароля */}
                    <div className="password-input-group">
                        <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Пароль"
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            className="auth-input"
                            autoComplete="current-password"
                        />
                        <button
                            type="button"
                            className="password-toggle"
                            onClick={() => setShowPassword(!showPassword)}
                        >
                            {showPassword ? <EyeSlashIcon style={{ width: 20, height: 20 }} /> : <EyeIcon style={{ width: 20, height: 20 }} />}
                        </button>
                    </div>

                    <div className="forgot-password">
                        <button
                            type="button"
                            onClick={handleForgotPassword}
                            className="forgot-password-link"
                        >
                            Забыли пароль?
                        </button>
                    </div>


                    <button type="submit" disabled={loading} className="auth-button">
                        {loading ? 'Загрузка...' : 'Войти'}
                    </button>
                </form>

                <div className="auth-switch">
                    <Link to="/register">
                        Нет аккаунта? Зарегистрируйтесь
                    </Link>
                </div>
            </div>

            <ResetPasswordModal
                isOpen={showResetModal}
                onClose={() => setShowResetModal(false)}
                onSend={handleSendPasswordReset}
            />
        </div>
    );
};